import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api, daysInMonth, exportCsv, fmtPct, monthLabel } from "@/lib/api";
import { Check } from "@/components/Check";
import { Progress } from "@/components/Progress";
import { Download, Plus, Trash2, ChevronLeft, ChevronRight } from "lucide-react";

function shiftMonth(ym, delta) {
  const [y, m] = ym.split("-").map(Number);
  const d = new Date(y, m - 1 + delta, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

export default function MonthView() {
  const [month, setMonth] = useState(null);
  const [habits, setHabits] = useState([]);
  const [entries, setEntries] = useState({}); // key habit_id|date -> bool
  const [newHabit, setNewHabit] = useState("");

  const loadAll = useCallback(async (m) => {
    const [hs, es] = await Promise.all([
      api.get("/habits").then((r) => r.data),
      api.get("/habits/entries", { params: { month: m } }).then((r) => r.data),
    ]);
    setHabits(Array.isArray(hs) ? hs : []);
    const map = {};
    for (const e of Array.isArray(es) ? es : []) {
      map[`${e.habit_id}|${e.date}`] = e.completed;
    }
    setEntries(map);
  }, []);

  useEffect(() => {
    (async () => {
      const s = await api.get("/settings").then((r) => r.data);
      setMonth(s.selected_month);
      await loadAll(s.selected_month);
    })();
  }, [loadAll]);

  const setMonthAndSave = async (m) => {
    setMonth(m);
    await api.put("/settings", { selected_month: m });
    await loadAll(m);
  };

  const days = month ? daysInMonth(month) : 0;
  const [y, m] = month ? month.split("-").map(Number) : [0, 0];
  const dayList = useMemo(
    () => Array.from({ length: days }, (_, i) => i + 1),
    [days]
  );

  const dayDate = (d) =>
    `${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;

  const toggle = async (habitId, day) => {
    const date = dayDate(day);
    const key = `${habitId}|${date}`;
    const next = !entries[key];
    setEntries((prev) => ({ ...prev, [key]: next }));
    try {
      await api.post("/habits/entries", {
        habit_id: habitId,
        date,
        completed: next,
      });
    } catch (e) {
      setEntries((prev) => ({ ...prev, [key]: !next }));
    }
  };

  const addHabit = async () => {
    const name = newHabit.trim();
    if (!name) return;
    const h = await api.post("/habits", { name }).then((r) => r.data);
    setHabits((prev) => [...prev, h]);
    setNewHabit("");
  };

  const removeHabit = async (id) => {
    if (!window.confirm("Delete this habit and all its history?")) return;
    await api.delete(`/habits/${id}`);
    setHabits((p) => p.filter((h) => h.id !== id));
    setEntries((prev) => {
      const c = { ...prev };
      Object.keys(c).forEach((k) => k.startsWith(id + "|") && delete c[k]);
      return c;
    });
  };

  const renameHabit = async (id, name) => {
    if (!name.trim()) return;
    await api.put(`/habits/${id}`, { name: name.trim() });
    setHabits((p) => p.map((h) => (h.id === id ? { ...h, name: name.trim() } : h)));
  };

  const habitStats = (h) => {
    let count = 0;
    for (let i = 1; i <= days; i++) {
      if (entries[`${h.id}|${dayDate(i)}`]) count += 1;
    }
    return { count, pct: days ? count / days : 0 };
  };

  const onExport = () => {
    const rows = habits.map((h) => {
      const r = { habit: h.name };
      for (let i = 1; i <= days; i++) {
        r[`d${i}`] = entries[`${h.id}|${dayDate(i)}`] ? 1 : 0;
      }
      const s = habitStats(h);
      r.completed = s.count;
      r.pct = Math.round(s.pct * 100);
      return r;
    });
    exportCsv(`month-${month}.csv`, rows);
  };

  const todayIso = new Date().toISOString().slice(0, 10);

  if (!month) return null;

  return (
    <div className="space-y-6" data-testid="month-view">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="kpi-label">Habit Grid</div>
          <h1 className="display text-3xl sm:text-5xl mt-3 leading-none font-bold uppercase tracking-tight">
            {monthLabel(month)}
          </h1>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setMonthAndSave(shiftMonth(month, -1))}
            className="hud-btn !px-2"
            data-testid="prev-month"
            aria-label="Previous month"
          >
            <ChevronLeft size={14} />
          </button>
          <button
            onClick={() => {
              const t = new Date();
              setMonthAndSave(
                `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, "0")}`
              );
            }}
            className="hud-btn"
            data-testid="this-month"
          >
            TODAY
          </button>
          <button
            onClick={() => setMonthAndSave(shiftMonth(month, 1))}
            className="hud-btn !px-2"
            data-testid="next-month"
            aria-label="Next month"
          >
            <ChevronRight size={14} />
          </button>
          <button
            onClick={onExport}
            className="hud-btn flex items-center gap-2"
            data-testid="export-month-csv"
          >
            <Download size={12} /> CSV
          </button>
        </div>
      </header>

      <div className="hud-frame overflow-hidden">
        <div className="overflow-x-auto">
          <table className="ox-table min-w-[1100px]">
            <thead>
              <tr>
                <th className="sticky left-0 bg-[hsl(var(--surface))] z-20 min-w-[200px]">
                  HABIT
                </th>
                {dayList.map((d) => {
                  const iso = dayDate(d);
                  const isToday = iso === todayIso;
                  return (
                    <th
                      key={d}
                      className="!text-center !px-1 min-w-[30px]"
                      style={isToday ? { color: "hsl(var(--oxblood))" } : null}
                      data-testid={`day-${d}`}
                    >
                      {d}
                    </th>
                  );
                })}
                <th className="!text-right min-w-[60px]">DONE</th>
                <th className="min-w-[140px]">PROGRESS</th>
                <th className="!text-right min-w-[50px]">%</th>
                <th className="!w-8" />
              </tr>
            </thead>
            <tbody>
              {habits.map((h) => {
                const s = habitStats(h);
                return (
                  <tr key={h.id} data-testid={`habit-row-${h.id}`}>
                    <td className="sticky left-0 bg-[hsl(var(--background))] z-10">
                      <input
                        defaultValue={h.name}
                        onBlur={(e) => renameHabit(h.id, e.target.value)}
                        className="hud-input !border-transparent hover:!border-border focus:!border-[hsl(var(--oxblood))] !py-1"
                        data-testid={`habit-name-${h.id}`}
                      />
                    </td>
                    {dayList.map((dd) => {
                      const checked = !!entries[`${h.id}|${dayDate(dd)}`];
                      return (
                        <td key={dd} className="!px-1 !py-1 text-center">
                          <div className="flex items-center justify-center">
                            <Check
                              size="sm"
                              checked={checked}
                              onChange={() => toggle(h.id, dd)}
                              testid={`cell-${h.id}-${dd}`}
                            />
                          </div>
                        </td>
                      );
                    })}
                    <td className="num">{s.count}</td>
                    <td>
                      <Progress value={s.pct} label={false} />
                    </td>
                    <td className="num">{fmtPct(s.pct)}</td>
                    <td>
                      <button
                        onClick={() => removeHabit(h.id)}
                        className="text-muted-foreground hover:text-[hsl(var(--signal))]"
                        data-testid={`delete-habit-${h.id}`}
                      >
                        <Trash2 size={13} />
                      </button>
                    </td>
                  </tr>
                );
              })}
              <tr>
                <td className="sticky left-0 bg-[hsl(var(--background))] z-10">
                  <div className="flex items-center gap-2">
                    <input
                      value={newHabit}
                      onChange={(e) => setNewHabit(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && addHabit()}
                      placeholder="+ Add habit…"
                      className="hud-input !py-1"
                      data-testid="new-habit-input"
                    />
                    <button
                      onClick={addHabit}
                      className="hud-btn !px-2 !py-1"
                      data-testid="add-habit"
                    >
                      <Plus size={12} />
                    </button>
                  </div>
                </td>
                <td colSpan={days + 4} />
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
